import { Formik, Form, Field, ErrorMessage } from 'formik'
import React, { useContext, useState, useEffect } from 'react'
import { BsEyeSlashFill } from '@react-icons/all-files/bs/BsEyeSlashFill'
import { BsEyeFill } from '@react-icons/all-files/bs/BsEyeFill'
import { useDispatch, useSelector } from 'react-redux'

import Button from 'components/common/button'
import { CheckBox } from 'components/common/checkbox'
import PhoneMaskedInputField from 'components/common/phoneNumber/maskedInput'
import { Spinner } from 'components/common/spinner'
import { RouteConstants } from 'config/routes/routeConstants'
import { LanguageContext } from 'context/LanguageContext'
import { LayoutContext } from 'context/LayoutContext'
import { resetNewUserFacebook } from 'modules/authentication/actions'
import {
    clearFormErrors,
    setAgeVerificationRequired,
    registerUserAccountMFA,
} from 'modules/user/actions'
import { util } from 'utils'
import { getCurrentCountry, countryCode, isValidUSPhoneNumber } from 'utils/appUtils'
import EnterOtp from 'components/signIn/EnterOtp'

const CreateAccount = props => {
    const { strings } = useContext(LanguageContext)

    const dispatch = useDispatch()
    const createAccountError = useSelector(state => state.user.getIn(['createUserProfileError']))
    const userProfile = useSelector(state => state.user.getIn(['userProfile']))
    const newUserFaceBook = useSelector(state => state.authentication.getIn(['newUserFaceBook']))
    const [loader, setLoader] = useState(false)
    const { closePanel, panelOpen } = useContext(LayoutContext)
    const locationInStore = useSelector(state => state.location)
    const userInStore = useSelector(state => state.user)
    const userCountry = getCurrentCountry(locationInStore, userInStore)
    const caTerms = useSelector(state => state.configuration.getIn(['ca_terms_url']))
    const usTerms = useSelector(state => state.configuration.getIn(['us_terms_url']))
    const caPrivacy = useSelector(state => state.configuration.getIn(['ca_privacy_url']))
    const usPrivacy = useSelector(state => state.configuration.getIn(['us_privacy_url']))
    const referAFriendEnabled = useSelector(state =>
        state.configuration.getIn(['refer_a_friend_enabled'])
    )
    const ageVerification = util.ageVerificationNeeded()
    const termsLink =
        userCountry === countryCode.CA
            ? strings.LINKS_canadaTermsAndConditions
            : strings.LINKS_TermsAndConditions
    const privacyLink =
        userCountry === countryCode.CA
            ? strings.LINKS_canadaPrivacyPolicy
            : strings.LINKS_PrivacyPolicy

    const showOTPScreen = useSelector(state => state.user.getIn(['showOTPScreen']))
    const otpData = useSelector(state => state.user.getIn(['otpData']))

    useEffect(() => {
        if (createAccountError) {
            setLoader(!loader)
        }
        if (userProfile !== null) {
            if (panelOpen) {
                closePanel()
            } else {
                props.hide()
            }
        }
    }, [createAccountError, userProfile])

    useEffect(() => {
        return () => {
            dispatch(resetNewUserFacebook())
            dispatch(clearFormErrors())
        }
    }, [])

    useEffect(() => {
        return () => {
            if (props.signInFromBasket) {
                if (panelOpen) {
                    closePanel()
                    if (ageVerification.isNeeded) {
                        dispatch(setAgeVerificationRequired(true))
                    }
                } else {
                    props.hide()
                }
            }
        }
    }, [])

    const getFormState = () => {
        if (
            newUserFaceBook &&
            newUserFaceBook.toJS().first_name &&
            newUserFaceBook.toJS().last_name
        ) {
            return {
                first_name: (newUserFaceBook && newUserFaceBook.toJS().first_name) || '',
                last_name: (newUserFaceBook && newUserFaceBook.toJS().last_name) || '',
                email: (newUserFaceBook && newUserFaceBook.toJS().email) || '',
                phone_number: '',
                marketingEmailNoty: false,
                accepts_us_terms: false,
                accepts_ca_terms: userCountry === countryCode.CA ? true : false,
                ageConfirmed: false,
                referralCode: '',
            }
        } else {
            return {
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                password: '',
                marketingEmailNoty: false,
                accepts_us_terms: false,
                accepts_ca_terms: userCountry === countryCode.CA ? true : false,
                ageConfirmed: false,
                referralCode: '',
            }
        }
    }

    const formState = getFormState()

    const onSubmit = userObj => {
        setLoader(true)
        dispatch(registerUserAccountMFA(new util.newUser(userObj)))
        dispatch(clearFormErrors())
    }

    const [hidePass, setHidePass] = useState(true)

    const handlePass = event => {
        event.preventDefault()
        setHidePass(!hidePass)
    }

    const closeRegistration = () => {
        if (panelOpen) {
            closePanel()
        } else {
            props.hide()
        }
    }

    const onSigninClicked = () => {
        props.setShowRegister(false)
    }

    if (showOTPScreen) {
        return <EnterOtp otpData={otpData} register={true} />
    }

    return (
        <div className={'createAcctForm'}>
            <Formik
                initialValues={formState}
                validationSchema={util.userValidationSchema(
                    newUserFaceBook && newUserFaceBook.toJS().first_name
                )}
                onSubmit={onSubmit}
            >
                {({ dirty, isValid, touched, errors, values }) => {
                    const isInValidPhoneNumber =
                        touched.phone_number &&
                        !errors.phone_number &&
                        values.phone_number &&
                        values.phone_number.length === 10 &&
                        isValidUSPhoneNumber(values.phone_number)

                    const showReferralTick =
                        values.referralCode &&
                        values.referralCode &&
                        values.referralCode.length === 6
                    const disabled = !dirty || !isValid || isInValidPhoneNumber

                    return (
                        <>
                            {loader ? (
                                <Spinner height={'5rem'} width={'5rem'} />
                            ) : (
                                <Form className={'createAcctForm__form'} autoComplete="off">
                                    <div className={'createAcctForm__head'}>
                                        <p className={'createAcctForm__head-main'}>
                                            {strings.registerButton}
                                        </p>
                                        <p className={'createAcctForm__head-sub'}>
                                            {strings.contactInformation}
                                        </p>
                                    </div>
                                    <div className={'createAcctForm__form__layer'}>
                                        <div className={'createAcctForm__form__inputs'}>
                                            <Field
                                                className={`createAcctForm__form__inputs-box ${
                                                    errors &&
                                                    errors.first_name &&
                                                    touched.first_name
                                                        ? `errorLine`
                                                        : null
                                                }`}
                                                type="text"
                                                name="first_name"
                                                placeholder={strings.firstName}
                                                autoComplete="none"
                                            />
                                            <ErrorMessage name="first_name">
                                                {msg => (
                                                    <div
                                                        className={
                                                            'createAcctForm__form__inputs-errormessage'
                                                        }
                                                    >
                                                        {msg}
                                                    </div>
                                                )}
                                            </ErrorMessage>
                                        </div>
                                        <div className={'createAcctForm__form__inputs'}>
                                            <Field
                                                className={`createAcctForm__form__inputs-box ${
                                                    errors && errors.last_name && touched.last_name
                                                        ? `errorLine`
                                                        : null
                                                }`}
                                                type="text"
                                                name="last_name"
                                                placeholder={strings.lastName}
                                                autoComplete="none"
                                            />
                                            <ErrorMessage name="last_name">
                                                {msg => (
                                                    <div
                                                        className={
                                                            'createAcctForm__form__inputs-errormessage'
                                                        }
                                                    >
                                                        {msg}
                                                    </div>
                                                )}
                                            </ErrorMessage>
                                        </div>
                                        <div className={'createAcctForm__form__inputs'}>
                                            <Field
                                                className={`createAcctForm__form__inputs-box ${
                                                    errors && errors.email && touched.email
                                                        ? `errorLine`
                                                        : null
                                                }`}
                                                type="email"
                                                name="email"
                                                placeholder={strings.emailPlaceHolder}
                                                autoComplete="none"
                                            />
                                            <ErrorMessage name="email">
                                                {msg => (
                                                    <div
                                                        className={
                                                            'createAcctForm__form__inputs-errormessage'
                                                        }
                                                    >
                                                        {msg}
                                                    </div>
                                                )}
                                            </ErrorMessage>
                                        </div>
                                        <div className={'createAcctForm__form__inputs'}>
                                            <PhoneMaskedInputField
                                                className="createAcctForm__form__inputs-box"
                                                hasError={
                                                    errors &&
                                                    errors.phone_number &&
                                                    touched.phone_number
                                                }
                                            />
                                            <ErrorMessage name="phone_number">
                                                {msg => (
                                                    <div
                                                        className={
                                                            'createAcctForm__form__inputs-errormessage'
                                                        }
                                                    >
                                                        {msg}
                                                    </div>
                                                )}
                                            </ErrorMessage>
                                        </div>
                                        {!newUserFaceBook && (
                                            <div className={'createAcctForm__form__inputs'}>
                                                <Field
                                                    className={`createAcctForm__form__inputs-box ${
                                                        errors &&
                                                        errors.password &&
                                                        touched.password
                                                            ? `errorLine`
                                                            : null
                                                    }`}
                                                    type={hidePass ? 'password' : 'text'}
                                                    name="password"
                                                    placeholder={strings.SIGN_IN_PASSWORD}
                                                    autoComplete="new-password"
                                                />
                                                <button
                                                    className={'createAcctForm__form__flipbutton'}
                                                    onClick={handlePass}
                                                    aria-label=" view / hide  password toggle"
                                                >
                                                    {hidePass ? (
                                                        <BsEyeSlashFill
                                                            className={
                                                                'createAcctForm__form__flipbutton-eyeIcon'
                                                            }
                                                        />
                                                    ) : (
                                                        <BsEyeFill
                                                            className={
                                                                'createAcctForm__form__flipbutton-eyeIcon'
                                                            }
                                                        />
                                                    )}
                                                </button>
                                                {errors && errors.password && touched.password ? (
                                                    <p
                                                        className={
                                                            'createAcctForm__form__inputs-errormessage'
                                                        }
                                                    >
                                                        {strings.passwordHint}
                                                    </p>
                                                ) : (
                                                    <p
                                                        className={
                                                            'createAcctForm__form__inputs-info'
                                                        }
                                                    >
                                                        {strings.passwordHint}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className={'createAcctForm__form__checks'}>
                                        <div>
                                            <CheckBox type="checkbox" name="ageConfirmed" />
                                            <label
                                                className={'createAcctForm__form__checks-text'}
                                                htmlFor="checkAge"
                                                aria-label="I'm over 13 years of age"
                                                tabIndex="0"
                                            >
                                                {strings.ageWarningMsg}
                                            </label>
                                        </div>
                                        {userCountry === countryCode.CA ? (
                                            <div className={'createAcctForm__form__checks-wrapper'}>
                                                <CheckBox
                                                    name={'marketingEmailNoty'}
                                                    type="checkbox"
                                                />
                                                <label
                                                    className={'createAcctForm__form__checks-text2'}
                                                    htmlFor="marketingEmailNoty"
                                                    aria-label="I agree to receive news promotions and information from 7-Eleven®. See our privay policy"
                                                    tabIndex="0"
                                                >
                                                    {strings.acceptEmailCommunication}
                                                    <a
                                                        className={
                                                            'createAcctForm__form__checks-link'
                                                        }
                                                        href={privacyLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {strings.privacyLink}
                                                    </a>
                                                    .
                                                </label>
                                            </div>
                                        ) : (
                                            <div className={'createAcctForm__form__checks-wrapper'}>
                                                <CheckBox
                                                    name={'accepts_us_terms'}
                                                    type="checkbox"
                                                />
                                                <label
                                                    className={'createAcctForm__form__checks-text'}
                                                    htmlFor="checkTerms"
                                                    aria-label="Yes, I accept the terms & conditions and privacy policy"
                                                    tabIndex="0"
                                                >
                                                    {strings.acceptTermMsg} &nbsp;
                                                    <a
                                                        className={
                                                            'createAcctForm__form__checks-link'
                                                        }
                                                        href={termsLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {strings.termsLink}
                                                    </a>
                                                    &nbsp; {strings.and} &nbsp;
                                                    <a
                                                        className={
                                                            'createAcctForm__form__checks-link'
                                                        }
                                                        href={privacyLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {strings.privacyLink}
                                                    </a>
                                                    .
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                    <div className={'createAcctForm__form-errorMsg'}>
                                        {createAccountError}
                                    </div>
                                    <div
                                        className={
                                            'enterAddressForm-actionBtns enterAddressForm-actionBtns-1 mt-2 '
                                        }
                                    >
                                        <Button
                                            type="submit"
                                            disabled={disabled}
                                            className={`confirm confirm-xl ${
                                                disabled && 'disabled'
                                            }`}
                                        >
                                            {strings.createAccountLabel}
                                        </Button>
                                    </div>
                                    {userCountry === countryCode.CA ? (
                                        <div className={'createAcctForm__form__emailNoty'}>
                                            <label
                                                className={'createAcctForm__form__emailNoty-text'}
                                                htmlFor="checkTermsCA"
                                                aria-label="By clicking Register, you accept our Canada"
                                                tabIndex="0"
                                            >
                                                {strings.createAccount1}
                                            </label>
                                            &nbsp;
                                            <a
                                                className={'createAcctForm__form__emailNoty-link'}
                                                href={termsLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {strings.termsLink}
                                            </a>
                                            &nbsp;
                                            <label
                                                className={'createAcctForm__form__emailNoty-text'}
                                            >
                                                and
                                            </label>{' '}
                                            &nbsp;
                                            <a
                                                className={'createAcctForm__form__emailNoty-link'}
                                                href={privacyLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {strings.privacyLink}
                                            </a>
                                        </div>
                                    ) : null}
                                    <div className="createAcctForm__toSignInForm">
                                        <p>{strings.alreadyHavingAccount}</p>
                                        <Button className="noBdyButton" onClick={onSigninClicked}>
                                            {strings.signinButton}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </>
                    )
                }}
            </Formik>
        </div>
    )
}

export default CreateAccount
